// Email Service - Frontend interface for email operations
// Emails are sent via Cloud Functions using Resend

import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc, setDoc, getDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const functions = getFunctions();

// Email template types
export type EmailTemplateType =
  | 'appointment-confirmation'
  | 'appointment-reminder'
  | 'lead-notification'
  | 'lead-with-appointment'
  | 'followup-appointment'
  | 'password-reset'
  | 'consultation-request'
  | 'automation-notification'
  | 'noshow-alert'
  | 'day-before-reminder'
  | 'invoice-sent'
  | 'payment-received'
  | 'contract-pending'
  | 'contract-reminder'
  | 'contract-signed'
  | 'training-assigned'
  | 'training-completed'
  | 'field-payment-processed'
  | 'welcome-user';

export interface EmailLog {
  id: string;
  to: string;
  from: string;
  subject: string;
  template: EmailTemplateType;
  templateData: Record<string, string>;
  status: 'pending' | 'sent' | 'failed';
  error?: string;
  sentAt?: Timestamp;
  createdAt: Timestamp;
}

export const emailService = {
  // Send test email to verify Resend configuration
  async sendTestEmail(recipientEmail: string): Promise<{ success: boolean; resendId?: string }> {
    const sendTestEmailFn = httpsCallable(functions, 'sendTestEmail');
    const result = await sendTestEmailFn({ recipientEmail });
    return result.data as { success: boolean; resendId?: string };
  },

  // Send password reset email via Cloud Function
  async sendPasswordReset(email: string): Promise<{ success: boolean }> {
    const sendCustomPasswordReset = httpsCallable(functions, 'sendCustomPasswordReset');
    const result = await sendCustomPasswordReset({ email });
    return result.data as { success: boolean };
  },

  // Request invoice email (triggers from billing page)
  async sendInvoiceEmail(invoiceId: string, recipientEmail: string): Promise<void> {
    // Get invoice details
    const invoiceDoc = await getDoc(doc(db, 'invoices', invoiceId));
    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }

    const invoice = invoiceDoc.data();

    // Queue email via mail collection (processed by Cloud Functions)
    await setDoc(doc(collection(db, 'mail')), {
      to: recipientEmail,
      template: 'invoice-sent',
      templateData: {
        recipientName: invoice.clientName || 'Client',
        invoiceNumber: invoice.invoiceNumber || invoiceId,
        amount: invoice.total?.toFixed(2) || '0.00',
        billingPeriod: invoice.billingPeriod || 'Current Period',
        dueDate: invoice.dueDate || 'Upon Receipt',
        invoiceUrl: `https://canvassed.com/invoices?id=${invoiceId}`,
        organizationName: invoice.organizationName || ''
      },
      createdAt: Timestamp.now(),
      status: 'pending'
    });
  },

  // Send contract notification
  async sendContractEmail(
    contractId: string,
    userId: string,
    type: 'pending' | 'reminder' | 'signed'
  ): Promise<void> {
    const contractDoc = await getDoc(doc(db, 'contracts', contractId));
    if (!contractDoc.exists()) {
      throw new Error('Contract not found');
    }

    const contract = contractDoc.data();

    const userDoc = await getDoc(doc(db, 'users', userId));
    const user = userDoc.data();

    if (!user?.email) {
      throw new Error('User email not found');
    }

    const templateData: Record<string, string> = {
      recipientName: user.name || `${user.firstName} ${user.lastName}`.trim() || 'User',
      contractTitle: contract.title || 'Employment Agreement',
      signUrl: `https://canvassed.com/contracts?sign=${contractId}`
    };

    if (type === 'reminder') {
      const createdAt = contract.createdAt?.toDate?.() || new Date();
      const daysPending = Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
      templateData.daysPending = String(daysPending);
    }

    if (type === 'signed') {
      templateData.signedDate = new Date().toLocaleDateString('en-US');
      templateData.signerName = templateData.recipientName;
    }

    if (contract.dueDate) {
      templateData.dueDate = contract.dueDate;
    }

    await setDoc(doc(collection(db, 'mail')), {
      to: user.email,
      template: `contract-${type}`,
      templateData,
      createdAt: Timestamp.now(),
      status: 'pending'
    });
  },

  // Send training assignment email
  async sendTrainingAssignedEmail(
    courseId: string,
    userId: string,
    dueDate?: string
  ): Promise<void> {
    const courseDoc = await getDoc(doc(db, 'courses', courseId));
    if (!courseDoc.exists()) {
      throw new Error('Course not found');
    }

    const course = courseDoc.data();

    const userDoc = await getDoc(doc(db, 'users', userId));
    const user = userDoc.data();

    if (!user?.email) {
      throw new Error('User email not found');
    }

    await setDoc(doc(collection(db, 'mail')), {
      to: user.email,
      template: 'training-assigned',
      templateData: {
        recipientName: user.name || `${user.firstName} ${user.lastName}`.trim() || 'User',
        courseName: course.title || course.name || 'Training Course',
        description: course.description || '',
        dueDate: dueDate || '',
        mandatory: course.mandatory ? 'true' : 'false',
        courseUrl: `https://canvassed.com/training?course=${courseId}`
      },
      createdAt: Timestamp.now(),
      status: 'pending'
    });
  },

  // Send training completion email
  async sendTrainingCompletedEmail(
    courseId: string,
    userId: string,
    score?: number,
    certificateUrl?: string
  ): Promise<void> {
    const courseDoc = await getDoc(doc(db, 'courses', courseId));
    if (!courseDoc.exists()) {
      throw new Error('Course not found');
    }

    const course = courseDoc.data();

    const userDoc = await getDoc(doc(db, 'users', userId));
    const user = userDoc.data();

    if (!user?.email) {
      throw new Error('User email not found');
    }

    await setDoc(doc(collection(db, 'mail')), {
      to: user.email,
      template: 'training-completed',
      templateData: {
        recipientName: user.name || `${user.firstName} ${user.lastName}`.trim() || 'User',
        courseName: course.title || course.name || 'Training Course',
        completedDate: new Date().toLocaleDateString('en-US'),
        score: score ? String(score) : '',
        certificateUrl: certificateUrl || ''
      },
      createdAt: Timestamp.now(),
      status: 'pending'
    });
  },

  // Send field payment notification
  async sendFieldPaymentEmail(
    userId: string,
    paymentData: {
      paymentPeriod: string;
      amount: number;
      validLeads: number;
      adjustments?: number;
      paymentMethod: string;
    }
  ): Promise<void> {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const user = userDoc.data();

    if (!user?.email) {
      throw new Error('User email not found');
    }

    await setDoc(doc(collection(db, 'mail')), {
      to: user.email,
      template: 'field-payment-processed',
      templateData: {
        recipientName: user.name || `${user.firstName} ${user.lastName}`.trim() || 'User',
        paymentPeriod: paymentData.paymentPeriod,
        amount: paymentData.amount.toFixed(2),
        validLeads: String(paymentData.validLeads),
        adjustments: paymentData.adjustments ? paymentData.adjustments.toFixed(2) : '',
        paymentMethod: paymentData.paymentMethod
      },
      createdAt: Timestamp.now(),
      status: 'pending'
    });
  },

  // Send welcome email to new user
  async sendWelcomeEmail(
    userId: string,
    tempPassword?: string
  ): Promise<void> {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const user = userDoc.data();

    if (!user?.email) {
      throw new Error('User email not found');
    }

    const roleLabels: Record<string, string> = {
      'provider_admin': 'Provider Administrator',
      'provider_user': 'Provider Staff',
      'client_admin': 'Client Administrator',
      'client_user': 'Client Staff',
      'field': 'Field Canvasser'
    };

    await setDoc(doc(collection(db, 'mail')), {
      to: user.email,
      template: 'welcome-user',
      templateData: {
        recipientName: user.name || `${user.firstName} ${user.lastName}`.trim() || 'User',
        email: user.email,
        role: roleLabels[user.role] || user.role,
        organizationName: user.organizationName || '',
        loginUrl: 'https://canvassed.com/login'
      },
      createdAt: Timestamp.now(),
      status: 'pending'
    });
  },

  // Send a lead-captured email to all project stakeholders when a lead is
  // created WITHOUT an appointment. When an appointment IS created, the
  // onAppointmentCreated cloud function handles the email, so this should
  // NOT be called in that case (avoids double emails).
  async sendLeadCapturedEmail(options: {
    projectId: string;
    projectName: string;
    leadName: string;
    phone?: string;
    email?: string;
    address?: string;
    capturedByName: string;
    leadId: string;
  }): Promise<void> {
    try {
      // Find all project stakeholders who should receive the email.
      const projectDoc = await getDoc(doc(db, 'projects', options.projectId));
      if (!projectDoc.exists()) return;
      const project = projectDoc.data();
      // Per-project toggle, DEFAULT OFF: no per-capture emails unless the
      // project explicitly enables them (settings.leadCapturedEmails).
      if (project?.settings?.leadCapturedEmails !== true) return;
      const assignedUsers = project?.assignedUsers || [];
      // Signature projects: the client gets the daily recap only — per-capture
      // emails stay internal so 10–20 signatures/day don't ping the client.
      const targetRoles = project?.settings?.signatureCapture
        ? ['provider_admin', 'provider_user']
        : ['provider_admin', 'provider_user', 'client_admin', 'client_user'];

      for (const au of assignedUsers) {
        if (!targetRoles.includes(au.role)) continue;
        const userDoc = await getDoc(doc(db, 'users', au.userId));
        if (!userDoc.exists()) continue;
        const user = userDoc.data();
        if (!user?.email) continue;
        // Respect email notification preferences.
        if (user.settings?.notifications?.email === false) continue;

        const subject = `New Lead: ${options.leadName} — ${options.projectName}`;
        const html = `
          <div style="font-family: Arial, sans-serif; color: #111; max-width: 560px;">
            <h2 style="color: #4f46e5; margin-bottom: 8px;">New Lead Captured</h2>
            <p>Hi ${user.name || 'there'},</p>
            <p>A new lead was captured for <strong>${options.projectName}</strong>.</p>
            <table style="margin: 16px 0; border-collapse: collapse;">
              <tr><td style="padding: 4px 12px 4px 0; color: #666;">Name</td><td style="padding: 4px 0;"><strong>${options.leadName}</strong></td></tr>
              ${options.phone ? `<tr><td style="padding: 4px 12px 4px 0; color: #666;">Phone</td><td style="padding: 4px 0;">${options.phone}</td></tr>` : ''}
              ${options.email ? `<tr><td style="padding: 4px 12px 4px 0; color: #666;">Email</td><td style="padding: 4px 0;">${options.email}</td></tr>` : ''}
              ${options.address ? `<tr><td style="padding: 4px 12px 4px 0; color: #666;">Address</td><td style="padding: 4px 0;">${options.address}</td></tr>` : ''}
              <tr><td style="padding: 4px 12px 4px 0; color: #666;">Captured by</td><td style="padding: 4px 0;">${options.capturedByName}</td></tr>
            </table>
            <p>
              <a href="https://canvassed-d9cdb.web.app/leads"
                 style="display: inline-block; padding: 10px 16px; background: #4f46e5; color: #fff; text-decoration: none; border-radius: 6px;">
                View in Canvassed
              </a>
            </p>
          </div>
        `;

        await setDoc(doc(collection(db, 'mail')), {
          to: user.email,
          message: { subject, html },
          createdAt: Timestamp.now(),
          status: 'pending',
        });
      }
    } catch (err) {
      console.error('Failed to send lead-captured email:', err);
      // Non-blocking — the lead was already saved.
    }
  },

  // Get email logs for admin review
  async getEmailLogs(filters?: {
    status?: 'pending' | 'sent' | 'failed';
    template?: EmailTemplateType;
    limit?: number;
  }): Promise<EmailLog[]> {
    let q = query(collection(db, 'mail'));

    if (filters?.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters?.template) {
      q = query(q, where('template', '==', filters.template));
    }

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as EmailLog));

    // Sort by createdAt descending
    logs.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    if (filters?.limit) {
      return logs.slice(0, filters.limit);
    }

    return logs;
  },

  // Get failed emails for retry
  async getFailedEmails(): Promise<EmailLog[]> {
    const q = query(collection(db, 'failedEmails'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as EmailLog));
  }
};

// Legacy function for backwards compatibility
export function getWelcomeMessage(userName: string, role: string): string {
  return `
Welcome to Canvassed, ${userName}!

You've been added as a ${role} to our canvassing platform.

Here's what you can do:
${role === 'field' ? '- Capture leads in the field\n- View your assigned walk lists\n- Track your progress' : ''}
${role === 'client_admin' ? '- Manage your team\n- View reports and analytics\n- Export lead data' : ''}
${role === 'provider_admin' ? '- Manage all users and projects\n- Access billing and settings\n- View comprehensive reports' : ''}

Your temporary password has been sent to your email. Please change it after logging in.

Questions? Contact your administrator.
`;
}
